import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MdCloudDownload } from "react-icons/md";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ActionIcon, Alert, Anchor, Center, Loader, Tooltip } from "@mantine/core";
import { removeAnchorLinks } from "./removeAnchorLinks";
import classes from "./CitationMarkdownViewer.module.css";

export interface CitationMarkdownViewerProps {
    src: string | undefined;
}

export const CitationMarkdownViewer = ({ src }: CitationMarkdownViewerProps) => {
    const [content, setContent] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const { t } = useTranslation();

    if (!src) {
        return null;
    }

    useEffect(() => {
        const fetchMarkdown = async () => {
            try {
                const response = await fetch(src);

                if (!response.ok) {
                    throw new Error("Failed loading markdown file.");
                }

                let markdownText = await response.text();
                markdownText = removeAnchorLinks(markdownText);
                setContent(markdownText);
            } catch (error: any) {
                setError(error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchMarkdown();
    }, [src]);

    return (
        <div>
            {isLoading ? (
                <Center>
                    <Loader size={16} type="dots" />
                </Center>
            ) : error ? (
                <Alert variant="light" color="red.6" radius="xs" title={error.message}>
                    <Tooltip label={t("tooltips.download")}>
                        <Anchor href={src} fs="xs" download={true}>
                            Download the file
                        </Anchor>
                    </Tooltip>
                </Alert>
            ) : (
                <div>
                    <Tooltip label={t("tooltips.save")}>
                        <Anchor href={src} download={true}>
                            <ActionIcon variant="transparent" color="dark.5">
                                <MdCloudDownload size={20} />
                            </ActionIcon>
                        </Anchor>
                    </Tooltip>
                    <div className={`${classes.markdown} ${classes.markdownViewer}`}>
                        <ReactMarkdown children={content} remarkPlugins={[remarkGfm]} />
                    </div>
                </div>
            )}
        </div>
    );
};
